import {
	Mesh,
	MeshStandardMaterial,
	RectAreaLight,
	Scene,
	SphereGeometry
} from 'three';

import {
	getQualitySegments,
	LIGHT_HELPER_DEFAULTS,
	RectAreaLightHelperObjects,
	RenderQuality
} from './light-helper.types';

/**
 * Creates visualization objects for a RectAreaLight (sphere only for now)
 *
 * RectAreaLight emits from a rectangular plane. Future enhancement:
 * visualize with a rectangle matching the light's width/height.
 *
 * @param light The RectAreaLight to visualize
 * @param scene Scene to add objects to
 * @param sphereRadius Radius of the sphere
 * @param visible Initial visibility state
 * @param quality Render quality 0-10
 * @returns RectAreaLight helper objects
 */
export const createRectAreaLightVisualization = (
	light: RectAreaLight,
	scene: Scene,
	sphereRadius: number = LIGHT_HELPER_DEFAULTS.CONE_RADIUS,
	visible: boolean = false,
	quality: RenderQuality = LIGHT_HELPER_DEFAULTS.QUALITY
): RectAreaLightHelperObjects => {
	const isLightOn = light.visible && light.intensity > 0;
	const segments = getQualitySegments(quality);

	// Create sphere at light position
	const sphereGeometry = new SphereGeometry(sphereRadius, segments.radial, segments.radial);
	const sphereColor = isLightOn
		? LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_ON
		: LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF;
	const sphereMaterial = new MeshStandardMaterial({ color: sphereColor, wireframe: false }); // Interface objects never wireframe
	const sphere = new Mesh(sphereGeometry, sphereMaterial);
	sphere.name = `rectAreaLightSphere_${light.uuid}`;
	sphere.position.copy(light.position);
	sphere.visible = visible;
	// Mark as interface object to exclude from wireframe mode
	sphere.userData.isInterface = true;
	sphere.userData.type = 'helper';
	scene.add(sphere);

	return { sphere };
};

/**
 * Updates RectAreaLight visualization objects
 */
export const updateRectAreaLightVisualization = (
	light: RectAreaLight,
	objects: RectAreaLightHelperObjects,
	visible: boolean
): void => {
	const isLightOn = light.visible && light.intensity > 0;

	// Update sphere position
	objects.sphere.position.copy(light.position);
	objects.sphere.visible = visible;

	// Update sphere color
	const sphereMaterial = objects.sphere.material as MeshStandardMaterial;
	sphereMaterial.color.setHex(
		isLightOn ? LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_ON : LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF
	);
};

/**
 * Disposes RectAreaLight visualization objects
 */
export const disposeRectAreaLightVisualization = (
	objects: RectAreaLightHelperObjects,
	scene: Scene
): void => {
	scene.remove(objects.sphere);
	objects.sphere.geometry.dispose();
	(objects.sphere.material as MeshStandardMaterial).dispose();
};

